/**
 * Messaging Service
 * Handles real-time group messaging, location sharing, and activity updates
 */

import { supabase } from '../lib/supabase';

/**
 * Send a text message to a group
 */
export async function sendMessage(groupId, senderId, content) {
  try {
    const { data, error } = await supabase?.from('group_messages')?.insert({
        group_id: groupId,
        sender_id: senderId,
        message_type: 'text',
        content: content,
        metadata: {}
      })?.select()?.single();

    if (error) throw error;
    return { success: true, data: convertMessageFromDb(data) };
  } catch (error) {
    console.error('Error sending message:', error);
    return { success: false, error: error?.message };
  }
}

/**
 * Get messages for a group
 */
export async function getGroupMessages(groupId, limit = 50) {
  try {
    const { data, error } = await supabase?.from('group_messages')?.select('*')?.eq('group_id', groupId)?.order('created_at', { ascending: false })?.limit(limit);

    if (error) throw error;
    return data?.map(convertMessageFromDb) || [];
  } catch (error) {
    console.error('Error fetching messages:', error);
    return [];
  }
}

/**
 * Share location with group
 */
export async function shareLocation(groupId, userId, latitude, longitude, locationName = null) {
  try {
    // Deactivate previous location shares
    await supabase?.from('location_shares')?.update({ is_active: false })?.eq('group_id', groupId)?.eq('user_id', userId);

    // Create new location share
    const { data: locationData, error: locationError } = await supabase?.from('location_shares')?.insert({
        group_id: groupId,
        user_id: userId,
        latitude: latitude,
        longitude: longitude,
        location_name: locationName,
        accuracy: 10.0,
        is_active: true,
        expires_at: new Date(Date.now() + 24 * 60 * 60 * 1000)?.toISOString() // 24 hours
      })?.select()?.single();

    if (locationError) throw locationError;

    // Send location message
    const { data: messageData, error: messageError } = await supabase?.from('group_messages')?.insert({
        group_id: groupId,
        sender_id: userId,
        message_type: 'location',
        content: locationName || 'Shared location',
        metadata: {
          location_id: locationData?.id,
          latitude: latitude,
          longitude: longitude,
          location_name: locationName
        }
      })?.select()?.single();

    if (messageError) throw messageError;

    return { 
      success: true, 
      location: convertLocationFromDb(locationData),
      message: convertMessageFromDb(messageData)
    };
  } catch (error) {
    console.error('Error sharing location:', error);
    return { success: false, error: error?.message };
  }
}

/**
 * Get active location shares for a group
 */
export async function getActiveLocations(groupId) {
  try {
    const { data, error } = await supabase?.from('location_shares')?.select('*')?.eq('group_id', groupId)?.eq('is_active', true)?.gte('expires_at', new Date()?.toISOString());

    if (error) throw error;
    return data?.map(convertLocationFromDb) || [];
  } catch (error) {
    console.error('Error fetching locations:', error);
    return [];
  }
}

/**
 * Create activity update
 */
export async function createActivityUpdate(groupId, userId, updateType, title, description = null, metadata = {}) {
  try {
    const { data: updateData, error: updateError } = await supabase?.from('activity_updates')?.insert({
        group_id: groupId,
        user_id: userId,
        update_type: updateType,
        title: title,
        description: description,
        metadata: metadata
      })?.select()?.single();

    if (updateError) throw updateError;

    // Send activity message
    const { data: messageData, error: messageError } = await supabase?.from('group_messages')?.insert({
        group_id: groupId,
        sender_id: userId,
        message_type: 'activity',
        content: title,
        metadata: {
          activity_id: updateData?.id,
          update_type: updateType,
          description: description
        }
      })?.select()?.single();

    if (messageError) throw messageError;

    return { 
      success: true, 
      activity: convertActivityFromDb(updateData),
      message: convertMessageFromDb(messageData)
    };
  } catch (error) {
    console.error('Error creating activity update:', error);
    return { success: false, error: error?.message };
  }
}

/**
 * Get activity updates for a group
 */
export async function getActivityUpdates(groupId, limit = 20) {
  try {
    const { data, error } = await supabase?.from('activity_updates')?.select('*')?.eq('group_id', groupId)?.order('created_at', { ascending: false })?.limit(limit);

    if (error) throw error;
    return data?.map(convertActivityFromDb) || [];
  } catch (error) {
    console.error('Error fetching activity updates:', error);
    return [];
  }
}

/**
 * Mark message as read
 */
export async function markMessageAsRead(messageId, userId) {
  try {
    const { error } = await supabase?.rpc('mark_message_read', {
      p_message_id: messageId,
      p_user_id: userId
    });

    if (error) throw error;
    return { success: true };
  } catch (error) {
    console.error('Error marking message as read:', error);
    return { success: false, error: error?.message };
  }
}

/**
 * Get unread message count
 */
export async function getUnreadMessageCount(groupId, userId) {
  try {
    const { data, error } = await supabase?.rpc('get_unread_message_count', {
      p_group_id: groupId,
      p_user_id: userId
    });

    if (error) throw error;
    return data || 0;
  } catch (error) {
    console.error('Error fetching unread count:', error);
    return 0;
  }
}

/**
 * Subscribe to real-time messages for a group
 */
export function subscribeToMessages(groupId, onMessage) {
  const channel = supabase?.channel(`messages_${groupId}`)?.on(
      'postgres_changes',
      {
        event: 'INSERT',
        schema: 'public',
        table: 'group_messages',
        filter: `group_id=eq.${groupId}`
      },
      (payload) => {
        const message = convertMessageFromDb(payload?.new);
        onMessage(message);
      }
    )?.subscribe();

  return channel;
}

/**
 * Subscribe to real-time location updates for a group
 */
export function subscribeToLocations(groupId, onLocation) {
  const channel = supabase?.channel(`locations_${groupId}`)?.on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'location_shares',
        filter: `group_id=eq.${groupId}`
      },
      (payload) => {
        const location = convertLocationFromDb(payload?.new);
        onLocation({ type: payload?.eventType, location });
      }
    )?.subscribe();

  return channel;
}

/**
 * Subscribe to real-time activity updates for a group
 */
export function subscribeToActivityUpdates(groupId, onActivity) {
  const channel = supabase?.channel(`activities_${groupId}`)?.on(
      'postgres_changes',
      {
        event: 'INSERT',
        schema: 'public',
        table: 'activity_updates',
        filter: `group_id=eq.${groupId}`
      },
      (payload) => {
        const activity = convertActivityFromDb(payload?.new);
        onActivity(activity);
      }
    )?.subscribe();

  return channel;
}

/**
 * Unsubscribe from real-time channel
 */
export function unsubscribeFromChannel(channel) {
  if (channel) {
    supabase?.removeChannel(channel);
  }
}

/**
 * Convert message from database format (snake_case) to app format (camelCase)
 */
function convertMessageFromDb(message) {
  if (!message) return null;
  return {
    id: message?.id,
    groupId: message?.group_id,
    senderId: message?.sender_id,
    messageType: message?.message_type,
    content: message?.content,
    metadata: message?.metadata || {},
    isRead: message?.is_read,
    createdAt: message?.created_at,
    updatedAt: message?.updated_at
  };
}

/**
 * Convert location from database format (snake_case) to app format (camelCase)
 */
function convertLocationFromDb(location) {
  if (!location) return null;
  return {
    id: location?.id,
    groupId: location?.group_id,
    userId: location?.user_id,
    latitude: parseFloat(location?.latitude),
    longitude: parseFloat(location?.longitude),
    locationName: location?.location_name,
    accuracy: parseFloat(location?.accuracy),
    sharedAt: location?.shared_at,
    expiresAt: location?.expires_at,
    isActive: location?.is_active
  };
}

/**
 * Convert activity from database format (snake_case) to app format (camelCase)
 */
function convertActivityFromDb(activity) {
  if (!activity) return null;
  return {
    id: activity?.id,
    groupId: activity?.group_id,
    userId: activity?.user_id,
    updateType: activity?.update_type,
    title: activity?.title,
    description: activity?.description,
    metadata: activity?.metadata || {},
    createdAt: activity?.created_at
  };
}